// =====================================================
// SIMPLIFIED CONTACT SERVICE
// =====================================================
// Uses the new simplified contacts_simple table
// Replaces the complex multi-table approach
// =====================================================

import { supabase } from '@/utils/supabase';

export interface SimpleContact {
  id: string;
  organizationId: string;
  
  // Enhanced Name Information
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  nickname?: string;
  suffix?: string;
  dateOfBirth?: string;
  
  // Enhanced Contact Information
  email?: string;
  workEmail?: string;
  phone?: string; // Fallback/primary phone
  mobilePhone?: string;
  workPhone?: string;
  homePhone?: string;
  fax?: string;
  linkedinUrl?: string;
  website?: string;
  
  // Contact Type & Status
  contactType: 'borrower' | 'referral_partner' | 'lead';
  status?: string;
  
  // Enhanced Mailing Address
  mailingStreet?: string;
  mailingUnit?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  
  // Fallback Address (kept for compatibility)
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  
  // Enhanced Credit Information
  creditScore?: number;
  creditScoreType?: string;
  creditScoreDate?: string;
  creditScoreExpirationDate?: string;
  
  // Co-Borrower / Spouse Information
  spouseInfo?: {
    title?: string;
    firstName?: string;
    lastName?: string;
    suffix?: string;
    email?: string;
    workEmail?: string;
    mobilePhone?: string;
    workPhone?: string;
    homePhone?: string;
    birthday?: string;
    anniversaryDate?: string;
    linkedContactId?: string; // Link to separate contact record
  };
  
  // Business/System Fields (kept for compatibility)
  company?: string;
  annualIncome?: number;
  loanAmount?: number;
  assignedLoanOfficerId?: string;
  notes?: string;
  tags?: string[];
  source?: string;
  lastContactDate?: string;
  optInMarketing?: boolean;
  optInSms?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  
  // Customer-Centric ID
  business_id?: string;
}

export interface ContactFilters {
  contactType?: 'borrower' | 'referral_partner' | 'lead';
  status?: string;
  search?: string;
  assignedLoanOfficerId?: string;
}

export class SimpleContactService {
  
  /**
   * Get all contacts
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async list(filters: ContactFilters = {}): Promise<SimpleContact[]> {
    let query = supabase
      .from('contacts_simple')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false});

    // Apply filters
    if (filters.contactType) {
      query = query.eq('contact_type', filters.contactType);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.search) {
      query = query.or(
        `first_name.ilike.%${filters.search}%,` +
        `middle_name.ilike.%${filters.search}%,` +
        `last_name.ilike.%${filters.search}%,` +
        `nickname.ilike.%${filters.search}%,` +
        `email.ilike.%${filters.search}%,` +
        `work_email.ilike.%${filters.search}%,` +
        `mobile_phone.ilike.%${filters.search}%,` +
        `work_phone.ilike.%${filters.search}%,` +
        `home_phone.ilike.%${filters.search}%,` +
        `company.ilike.%${filters.search}%,` +
        `mailing_street.ilike.%${filters.search}%,` +
        `mailing_city.ilike.%${filters.search}%,` +
        `mailing_state.ilike.%${filters.search}%,` +
        `address.ilike.%${filters.search}%,` +
        `city.ilike.%${filters.search}%,` +
        `state.ilike.%${filters.search}%`
      );
    }

    if (filters.assignedLoanOfficerId) {
      query = query.eq('assigned_loan_officer_id', filters.assignedLoanOfficerId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Database query failed:', error);
      throw error;
    }

    return (data || []).map(this.mapToContact);
  }

  /**
   * Get contact by ID
   */
  async getById(id: string): Promise<SimpleContact | null> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
return null;
} // Not found
      throw error;
    }

    return this.mapToContact(data);
  }

  /**
   * Create a new contact
   */
  async create(contactData: Partial<SimpleContact>): Promise<SimpleContact> {
    // Get current user and their organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (profileError || !profile) {
      throw new Error('User profile not found');
    }

    const { data, error } = await supabase
      .from('contacts_simple')
      .insert({
        organization_id: profile.organization_id,
        
        // Enhanced Name Information
        title: contactData.title,
        first_name: contactData.firstName,
        middle_name: contactData.middleName,
        last_name: contactData.lastName,
        nickname: contactData.nickname,
        suffix: contactData.suffix,
        date_of_birth: contactData.dateOfBirth,
        
        // Enhanced Contact Information
        email: contactData.email,
        work_email: contactData.workEmail,
        mobile_phone: contactData.mobilePhone,
        work_phone: contactData.workPhone,
        home_phone: contactData.homePhone,
        fax: contactData.fax,
        linkedin_url: contactData.linkedinUrl,
        website: contactData.website,
        
        // Contact Type & Status
        contact_type: contactData.contactType || 'borrower',
        status: contactData.status || 'new',
        
        // Enhanced Mailing Address
        mailing_street: contactData.mailingStreet,
        mailing_unit: contactData.mailingUnit,
        mailing_city: contactData.mailingCity,
        mailing_state: contactData.mailingState,
        mailing_zip: contactData.mailingZip,
        
        // Fallback Address (kept for compatibility)
        address: contactData.address,
        city: contactData.city,
        state: contactData.state,
        zip: contactData.zip,
        
        // Enhanced Credit Information
        credit_score: contactData.creditScore,
        credit_score_type: contactData.creditScoreType,
        credit_score_date: contactData.creditScoreDate,
        credit_score_expiration_date: contactData.creditScoreExpirationDate,
        
        // Spouse Information
        spouse_info: contactData.spouseInfo,
        
        // Business/System Fields (kept for compatibility)
        company: contactData.company,
        annual_income: contactData.annualIncome,
        loan_amount: contactData.loanAmount,
        // Default assigned loan officer to current user (creator of contact)
        assigned_loan_officer_id: contactData.assignedLoanOfficerId || user.id,
        notes: contactData.notes,
        tags: contactData.tags || [],
        source: contactData.source || 'manual',
        opt_in_marketing: contactData.optInMarketing ?? true,
        opt_in_sms: contactData.optInSms ?? false
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A contact with this email already exists');
      }
      throw error;
    }

    return this.mapToContact(data);
  }

  /**
   * Update contact
   */
  async update(id: string, updates: Partial<SimpleContact>): Promise<SimpleContact> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .update({
        // Enhanced Name Information
        title: updates.title,
        first_name: updates.firstName,
        middle_name: updates.middleName,
        last_name: updates.lastName,
        nickname: updates.nickname,
        suffix: updates.suffix,
        date_of_birth: updates.dateOfBirth,
        
        // Enhanced Contact Information
        email: updates.email,
        work_email: updates.workEmail,
        mobile_phone: updates.mobilePhone,
        work_phone: updates.workPhone,
        home_phone: updates.homePhone,
        fax: updates.fax,
        linkedin_url: updates.linkedinUrl,
        website: updates.website,
        
        // Contact Type & Status
        contact_type: updates.contactType,
        status: updates.status,
        
        // Enhanced Mailing Address
        mailing_street: updates.mailingStreet,
        mailing_unit: updates.mailingUnit,
        mailing_city: updates.mailingCity,
        mailing_state: updates.mailingState,
        mailing_zip: updates.mailingZip,
        
        // Fallback Address (kept for compatibility)
        address: updates.address,
        city: updates.city,
        state: updates.state,
        zip: updates.zip,
        
        // Enhanced Credit Information
        credit_score: updates.creditScore,
        credit_score_type: updates.creditScoreType,
        credit_score_date: updates.creditScoreDate,
        credit_score_expiration_date: updates.creditScoreExpirationDate,
        
        // Spouse Information
        spouse_info: updates.spouseInfo,
        
        // Business/System Fields (kept for compatibility)
        company: updates.company,
        annual_income: updates.annualIncome,
        loan_amount: updates.loanAmount,
        assigned_loan_officer_id: updates.assignedLoanOfficerId,
        notes: updates.notes,
        tags: updates.tags,
        source: updates.source,
        last_contact_date: updates.lastContactDate,
        opt_in_marketing: updates.optInMarketing,
        opt_in_sms: updates.optInSms
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
throw error;
}
    return this.mapToContact(data);
  }

  /**
   * Soft delete contact
   */
  async softDelete(id: string): Promise<{ success: boolean; message: string }> {
    const { data, error } = await supabase.rpc('delete_contact_simple', {
      p_contact_id: id
    });

    if (error) {
      console.error('Soft delete error:', error);
      throw new Error(error.message || 'Failed to delete contact');
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete contact');
    }

    return { success: true, message: data.message };
  }

  /**
   * Hard delete contact (admin only)
   */
  async hardDelete(id: string): Promise<{ success: boolean; message: string }> {
    const { data, error } = await supabase.rpc('hard_delete_contact_simple', {
      p_contact_id: id
    });

    if (error) {
      console.error('Hard delete error:', error);
      throw new Error(error.message || 'Failed to delete contact');
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete contact');
    }

    return { success: true, message: data.message };
  }

  /**
   * Get contacts for leads view (contactType = 'lead' OR status suggests lead)
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async getLeads(): Promise<SimpleContact[]> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .or('contact_type.eq.lead,status.in.(new,active,contacted,qualifying)')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}
    return (data || []).map(this.mapToContact);
  }

  /**
   * Get contacts for borrowers view (contactType = 'borrower' with completed status)
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async getBorrowers(): Promise<SimpleContact[]> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('contact_type', 'borrower')
      .in('status', ['funded', 'closed', 'completed'])
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}
    return (data || []).map(this.mapToContact);
  }

  /**
   * Get contacts for referral partners view
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async getReferralPartners(): Promise<SimpleContact[]> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('contact_type', 'referral_partner')
      .is('deleted_at', null)
      .order('created_at', { ascending: false});

    if (error) {
throw error;
}
    return (data || []).map(this.mapToContact);
  }

  /**
   * Search contacts by name or email
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async search(query: string): Promise<SimpleContact[]> {
    const searchTerm = `%${query.toLowerCase()}%`;

    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .is('deleted_at', null)
      .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},email.ilike.${searchTerm}`)
      .limit(10)
      .order('first_name', { ascending: true });

    if (error) {
      throw error;
    }
    return (data || []).map(this.mapToContact);
  }

  /**
   * Get contacts with their loan history from borrowers table
   * This properly identifies past customers based on actual funded loans
   * Note: RLS policies filter by assigned_loan_officer_id = auth.uid()
   */
  async listWithLoanHistory(): Promise<(SimpleContact & {
    loanHistory: Array<{
      id: string;
      contact_id: string;
      loan_number: string;
      funded_amount: number;
      interest_rate: number;
      closing_date: string;
      property_address: string;
      is_current: boolean;
      lifetime_loan_count: number;
    }>;
    isPastCustomer: boolean;
    totalLoans: number;
    totalFundedAmount: number;
    lastLoanDate?: string;
  })[]> {
    // First get all contacts (RLS filters by assigned_loan_officer_id)
    const { data: contacts, error: contactsError } = await supabase
      .from('contacts_simple')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (contactsError) {
throw contactsError;
}

    // Then get all borrower records (funded loans)
    const { data: borrowers, error: borrowersError } = await supabase
      .from('borrowers')
      .select(`
        id,
        contact_id,
        loan_number,
        funded_amount,
        interest_rate,
        closing_date,
        property_address,
        is_current,
        lifetime_loan_count
      `)
      .not('closing_date', 'is', null)
      .order('closing_date', { ascending: false });

    if (borrowersError) {
throw borrowersError;
}

    // Map contacts with their loan history
    return (contacts || []).map(contact => {
      const contactLoans = (borrowers || []).filter(b => b.contact_id === contact.id);
      const totalFundedAmount = contactLoans.reduce((sum, loan) => sum + (loan.funded_amount || 0), 0);
      const lastLoan = contactLoans[0]; // Already sorted by closing_date desc

      return {
        ...this.mapToContact(contact),
        loanHistory: contactLoans,
        isPastCustomer: contactLoans.length > 0,
        totalLoans: contactLoans.length,
        totalFundedAmount,
        lastLoanDate: lastLoan?.closing_date
      };
    });
  }

  /**
   * Check if an email already exists in the organization
   * @param email - The email to check
   * @returns The existing contact if found, null otherwise
   */
  async checkEmailExists(email: string | undefined): Promise<SimpleContact | null> {
    if (!email) {
return null;
}

    // Get current user's organization
    const organizationId = await this.getUserOrganizationId();

    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('organization_id', organizationId)
      .ilike('email', email) // Case-insensitive comparison
      .is('deleted_at', null) // Only check non-deleted contacts
      .single();

    if (error) {
      // 'PGRST116' is the error code for no rows returned
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error checking email existence:', error);
      return null;
    }

    return data ? this.mapToContact(data) : null;
  }

  /**
   * Check if a phone number already exists in the organization
   * Returns the existing contact if found, null otherwise
   * Normalizes phone to last 10 digits for comparison
   */
  async checkPhoneExists(phone: string | undefined, excludeId?: string): Promise<SimpleContact | null> {
    if (!phone) {
return null;
}

    // Normalize phone to last 10 digits
    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    if (normalizedPhone.length < 10) {
      return null; // Invalid phone
    }

    // Get current user's organization
    const organizationId = await this.getUserOrganizationId();

    // Get all contacts with phones in this organization
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('organization_id', organizationId)
      .not('mobile_phone', 'is', null)
      .is('deleted_at', null);

    if (error || !data) {
      console.error('Error checking phone existence:', error);
      return null;
    }

    // Find a matching contact (last 10 digits)
    const match = data.find(contact => {
      if (!contact.mobile_phone) {
return false;
}
      if (excludeId && contact.id === excludeId) {
return false;
}
      const contactPhone = contact.mobile_phone.replace(/\D/g, '').slice(-10);
      return contactPhone === normalizedPhone;
    });

    return match ? this.mapToContact(match) : null;
  }

  /**
   * Get current user's organization ID
   * Used for create() to set organization_id when inserting new contacts
   */
  private async getUserOrganizationId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data: profile, error } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (error || !profile?.organization_id) {
      console.error('❌ Organization not found:', { error, profile, userId: user.id });
      throw new Error('Organization not found for user');
    }

    return profile.organization_id;
  }

  /**
   * Map database record to SimpleContact
   */
  private mapToContact(data: Record<string, unknown>): SimpleContact {
    return {
      id: data.id,
      organizationId: data.organization_id,
      
      // Enhanced Name Information
      title: data.title,
      firstName: data.first_name,
      middleName: data.middle_name,
      lastName: data.last_name,
      nickname: data.nickname,
      suffix: data.suffix,
      dateOfBirth: data.date_of_birth,
      
      // Enhanced Contact Information
      email: data.email,
      workEmail: data.work_email,
      mobilePhone: data.mobile_phone,
      workPhone: data.work_phone,
      homePhone: data.home_phone,
      fax: data.fax,
      linkedinUrl: data.linkedin_url,
      website: data.website,
      
      // Contact Type & Status
      contactType: data.contact_type,
      status: data.status,
      
      // Enhanced Mailing Address
      mailingStreet: data.mailing_street,
      mailingUnit: data.mailing_unit,
      mailingCity: data.mailing_city,
      mailingState: data.mailing_state,
      mailingZip: data.mailing_zip,
      
      // Fallback Address (kept for compatibility)
      address: data.address,
      city: data.city,
      state: data.state,
      zip: data.zip,
      
      // Enhanced Credit Information
      creditScore: data.credit_score,
      creditScoreType: data.credit_score_type,
      creditScoreDate: data.credit_score_date,
      creditScoreExpirationDate: data.credit_score_expiration_date,
      
      // Spouse Information
      spouseInfo: data.spouse_info,
      
      // Business/System Fields (kept for compatibility)
      company: data.company,
      annualIncome: data.annual_income,
      loanAmount: data.loan_amount,
      assignedLoanOfficerId: data.assigned_loan_officer_id,
      notes: data.notes,
      tags: data.tags || [],
      source: data.source,
      lastContactDate: data.last_contact_date,
      optInMarketing: data.opt_in_marketing,
      optInSms: data.opt_in_sms,
      deletedAt: data.deleted_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      
      // Customer-Centric ID
      business_id: data.business_id
    };
  }
}

// Export singleton instance
export const simpleContactService = new SimpleContactService();